/**
 * @fileoverview L-BFGS search-direction generator (limited-memory BFGS).
 *
 * Implements the standard two-loop recursion (Nocedal & Wright, "Numerical
 * Optimization", 2nd ed., Algorithm 7.4) over a ring buffer of the most
 * recent `m` curvature pairs (s, y) and their reciprocal inner products
 * rho = 1 / (y . s).
 *
 * This class is purely the *direction* part of the minimizer: it owns no
 * coordinates or energy function. The driver (`minimize.js`, a different
 * module) calls `update(s, y)` after each accepted step and
 * `computeDirection(grad, out)` to obtain the next search direction
 * `-H . g` (an approximation of the Newton step). On line-search failure
 * the driver calls `reset()` to discard the (now-stale) history and fall
 * back to steepest descent for one step.
 *
 * Units: the class is unit-agnostic — it operates on raw `Float64Array`
 * vectors. In engine use, `grad`/`y` are in kJ/mol/nm, `s` in nm, and the
 * returned direction is in nm.
 *
 * Hot-path discipline: all storage is preallocated in the constructor.
 * `update()` and `computeDirection()` perform no heap allocation — only
 * writes into preallocated buffers.
 */

/**
 * Curvature-safety threshold for the inner product y . s. A pair with
 * y . s at or below this value violates the curvature condition (the BFGS
 * update would not stay positive-definite), so it is skipped. Scaled
 * relative to ||y||^2 inside `update()`; this is the absolute floor.
 *
 * @type {number}
 */
const CURVATURE_EPS = 1e-12;

/**
 * Limited-memory BFGS direction generator.
 *
 * Storage layout: three ring buffers of capacity `m`, each a flat
 * `Float64Array` of length `m * n`. Slot `k` of the s-buffer occupies
 * `[k*n, (k+1)*n)`. The `rho` and per-slot scalars live in small
 * length-`m` arrays. A monotonically increasing `count` tracks how many
 * pairs have been pushed; the oldest live slot is `count - size`.
 */
export class LBFGS {
    /**
     * @param {number} n   State-vector length (e.g. 3 * nAtoms).
     * @param {number} [m=5]   History size (number of curvature pairs kept).
     */
    constructor(n, m = 5) {
        if (!Number.isInteger(n) || n <= 0) {
            throw new Error(`LBFGS: n must be a positive integer, got ${n}`);
        }
        if (!Number.isInteger(m) || m <= 0) {
            throw new Error(`LBFGS: m must be a positive integer, got ${m}`);
        }

        /** @type {number} State-vector length. */
        this.n = n;
        /** @type {number} History capacity. */
        this.m = m;

        /** @type {Float64Array} Ring buffer of s = x_{k+1} - x_k, m*n. */
        this.sBuf = new Float64Array(m * n);
        /** @type {Float64Array} Ring buffer of y = g_{k+1} - g_k, m*n. */
        this.yBuf = new Float64Array(m * n);
        /** @type {Float64Array} rho[slot] = 1 / (y . s), length m. */
        this.rho = new Float64Array(m);

        /**
         * @type {Float64Array} Scratch for the per-slot alpha coefficients
         * used in the two-loop recursion, length m.
         */
        this.alpha = new Float64Array(m);

        /**
         * @type {number} Total number of pairs ever pushed (including those
         * overwritten by the ring). The live slot for the j-th most recent
         * pair (j = 0 newest) is `(count - 1 - j) % m`.
         */
        this.count = 0;
    }

    /** @returns {number} Number of curvature pairs currently stored (0..m). */
    get size() {
        return this.count < this.m ? this.count : this.m;
    }

    /** Discard all stored curvature pairs. */
    reset() {
        this.count = 0;
        // No need to zero the buffers; `size`/`count` gate all reads.
    }

    /**
     * Push a new curvature pair into the ring buffer.
     *
     * Computes rho = 1 / (y . s). If the curvature condition fails
     * (y . s <= eps * ||y||^2, or y . s <= CURVATURE_EPS), the pair is
     * skipped — the history is left unchanged. This guards positive
     * definiteness of the implicit Hessian approximation: a non-positive
     * y . s would otherwise corrupt the two-loop recursion.
     *
     * @param {Float64Array} s   x_{k+1} - x_k, length n.
     * @param {Float64Array} y   g_{k+1} - g_k, length n.
     * @returns {boolean}   true if the pair was stored, false if skipped by
     *                      the curvature guard.
     */
    update(s, y) {
        const { n } = this;

        let ys = 0;
        let yy = 0;
        for (let i = 0; i < n; i++) {
            const yi = y[i];
            ys += yi * s[i];
            yy += yi * yi;
        }

        // Curvature guard: require y . s to be meaningfully positive,
        // relative to ||y||^2 (so the test is scale-aware) and above an
        // absolute floor (so a near-zero y doesn't sneak through).
        const relFloor = 1e-10 * yy;
        if (!(ys > CURVATURE_EPS) || !(ys > relFloor)) {
            return false;
        }

        const slot = this.count % this.m;
        const base = slot * n;
        this.sBuf.set(s, base);
        this.yBuf.set(y, base);
        this.rho[slot] = 1 / ys;
        this.count++;
        return true;
    }

    /**
     * Compute the search direction `out = -H . grad`, where H is the
     * implicit limited-memory inverse-Hessian approximation built from the
     * stored curvature pairs.
     *
     * On empty history (first iteration, or just after `reset()`), there is
     * no curvature information, so this falls back to steepest descent
     * scaled by the inf-norm of the gradient:
     *
     *     out = -grad / ||grad||_inf
     *
     * This gives a unit-ish initial step well-suited to the α=1 starting
     * guess of the backtracking line search. If the gradient is exactly
     * zero (already at a stationary point), `out` is filled with zeros.
     *
     * No heap allocation: writes only into `out` and the preallocated
     * `this.alpha` scratch.
     *
     * @param {Float64Array} grad   Current gradient g, length n.
     * @param {Float64Array} out    Output buffer for the direction, length n.
     * @returns {void}
     */
    computeDirection(grad, out) {
        const { n, m } = this;
        const size = this.size;

        if (size === 0) {
            // Steepest descent scaled by 1/||g||_inf.
            let infNorm = 0;
            for (let i = 0; i < n; i++) {
                const a = grad[i] < 0 ? -grad[i] : grad[i];
                if (a > infNorm) infNorm = a;
            }
            if (infNorm === 0) {
                out.fill(0);
                return;
            }
            const inv = 1 / infNorm;
            for (let i = 0; i < n; i++) out[i] = -grad[i] * inv;
            return;
        }

        // Two-loop recursion. Start q = grad; the result -H.g is returned.
        // out doubles as the working vector q.
        out.set(grad);

        // First loop: newest -> oldest.
        // The j-th most recent pair (j=0 newest) lives in slot
        // (count - 1 - j) % m. We also need gamma = (s_newest . y_newest) /
        // (y_newest . y_newest) for the initial Hessian scaling H0 = gamma*I.
        const newestSlot = (this.count - 1) % m;
        for (let j = 0; j < size; j++) {
            let slot = (this.count - 1 - j) % m;
            if (slot < 0) slot += m;
            const base = slot * n;
            let sq = 0;
            for (let i = 0; i < n; i++) sq += this.sBuf[base + i] * out[i];
            const a = this.rho[slot] * sq;
            this.alpha[slot] = a;
            for (let i = 0; i < n; i++) out[i] -= a * this.yBuf[base + i];
        }

        // Initial Hessian scaling: gamma = (s_k . y_k) / (y_k . y_k) for the
        // most recent pair. rho[newest] = 1/(y.s), so s.y = 1/rho.
        {
            const base = newestSlot * n;
            let yy = 0;
            for (let i = 0; i < n; i++) {
                const yi = this.yBuf[base + i];
                yy += yi * yi;
            }
            const sy = 1 / this.rho[newestSlot];
            const gamma = yy > 0 ? sy / yy : 1;
            for (let i = 0; i < n; i++) out[i] *= gamma;
        }

        // Second loop: oldest -> newest.
        for (let j = size - 1; j >= 0; j--) {
            let slot = (this.count - 1 - j) % m;
            if (slot < 0) slot += m;
            const base = slot * n;
            let yr = 0;
            for (let i = 0; i < n; i++) yr += this.yBuf[base + i] * out[i];
            const beta = this.rho[slot] * yr;
            const coef = this.alpha[slot] - beta;
            for (let i = 0; i < n; i++) out[i] += coef * this.sBuf[base + i];
        }

        // out currently holds H.g; the search direction is -H.g.
        for (let i = 0; i < n; i++) out[i] = -out[i];
    }
}
